/**
 * App entry — providers, routing, and lazy-loading.
 *
 * Every route component is wrapped in a `RouteErrorBoundary` so a runtime
 * error inside one page doesn't blank the whole app, and is loaded via
 * `React.lazy` so Vite can split each surface into its own chunk. The
 * Suspense fallback is intentionally minimal (a centred dot pulse) — the
 * UX target is "page transition feels instant on a fast network" without
 * a flash of skeleton on every navigation.
 *
 * Auth-flow routes (Auth / AuthCallback / Reset / Invite / NotFound) are
 * NOT lazy-loaded because they're either rare or critical: rare → no
 * benefit from splitting; critical → faster bundle parse on the first
 * paint after a stale cookie redirect.
 */
import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";

// ---- Eager auth-flow surfaces (small bundles, almost-always-needed) -------
import NotFound from "./pages/NotFound.tsx";
import AuthPage from "./pages/Auth.tsx";
import AuthCallback from "./pages/AuthCallback.tsx";
import ForgotPassword from "./pages/ForgotPassword.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import VerifyEmail from "./pages/VerifyEmail.tsx";
import Invite from "./pages/Invite.tsx";
import NoPermission from "./pages/NoPermission.tsx";

// ---- Lazy public surfaces (small content pages, but split anyway) ---------
const Privacy       = lazy(() => import("./pages/Privacy.tsx"));
const Terms         = lazy(() => import("./pages/Terms.tsx"));
const Demo          = lazy(() => import("./pages/Demo.tsx"));

// ---- Lazy in-app surfaces (split each into its own chunk) -----------------
const Index         = lazy(() => import("./pages/Index.tsx"));
const Orders        = lazy(() => import("./pages/Orders.tsx"));
const Alerts        = lazy(() => import("./pages/Alerts.tsx"));
const ClaudePage    = lazy(() => import("./pages/Claude.tsx"));
const SettingsPage  = lazy(() => import("./pages/Settings.tsx"));
const SettingsTeam  = lazy(() => import("./pages/SettingsTeam.tsx"));
const SettingsAudit = lazy(() => import("./pages/SettingsAudit.tsx"));
const Analytics     = lazy(() => import("./pages/Analytics.tsx"));
const Shipments     = lazy(() => import("./pages/Shipments.tsx"));
const Inventory     = lazy(() => import("./pages/Inventory.tsx"));
const Onboarding    = lazy(() => import("./pages/Onboarding.tsx"));
const Playground    = lazy(() => import("./pages/Playground.tsx"));

// ---- Lazy "Natural extensions" surfaces (RTO-prevention / recovery) -------
const PrepaidConversion = lazy(() => import("./pages/PrepaidConversion.tsx"));
const RiskScoring       = lazy(() => import("./pages/RiskScoring.tsx"));
const NDR               = lazy(() => import("./pages/NDR.tsx"));
const Routing           = lazy(() => import("./pages/Routing.tsx"));
const FreightRecon      = lazy(() => import("./pages/FreightRecon.tsx"));

// ---- Lazy "Marketplace expansion" surfaces (Amazon/Flipkart/Meesho/Myntra) -
const Channels            = lazy(() => import("./pages/Channels.tsx"));
const ChannelOrders       = lazy(() => import("./pages/ChannelOrders.tsx"));
const ChannelReturns      = lazy(() => import("./pages/ChannelReturns.tsx"));
const ChannelSettlements  = lazy(() => import("./pages/ChannelSettlements.tsx"));

// QueryClient defaults tuned for offline / unreachable cases:
//   - refetchOnReconnect: TanStack Query refetches every active query when
//     navigator.online flips to true. OfflineBanner also invalidates on
//     the captive-portal recovery edge so both transition points are covered.
//   - retry: 2 retries before surfacing the error. With our `withRetry`
//     helper on the server side this is mostly for transient connection
//     blips during a deploy.
//   - staleTime: 30s default so back-to-back navigations don't refetch.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnReconnect:   true,
      refetchOnWindowFocus: false,
      retry:        2,
      retryDelay:   (attempt) => Math.min(1000 * 2 ** attempt, 8_000),
      staleTime:    30_000,
    },
    mutations: {
      retry: 0, // mutations never retry silently — the user re-clicks if needed
    },
  },
});

/** Render a lazy route inside its own error boundary + suspense fallback. */
function RouteShell({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) {
  return (
    <RouteErrorBoundary routeName={name}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" aria-live="polite">
      <span className="sr-only">Loading…</span>
      <span className="relative inline-flex h-3 w-3">
        <span className="absolute inset-0 animate-ping rounded-full bg-accent/60" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-accent" />
      </span>
    </div>
  );
}

const protect = (name: string, el: JSX.Element) => (
  <RouteShell name={name}>
    <ProtectedRoute>{el}</ProtectedRoute>
  </RouteShell>
);

const App = () => (
  <AppErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <Routes>
              {/* Public surfaces — no AppLayout, no auth required. */}
              <Route path="/privacy"          element={<Suspense fallback={<RouteFallback />}><Privacy /></Suspense>} />
              <Route path="/terms"            element={<Suspense fallback={<RouteFallback />}><Terms /></Suspense>} />
              <Route path="/demo"             element={<Suspense fallback={<RouteFallback />}><Demo /></Suspense>} />
              <Route path="/auth"             element={<AuthPage />} />
              <Route path="/auth/callback"    element={<AuthCallback />} />
              <Route path="/forgot-password"  element={<ForgotPassword />} />
              <Route path="/reset-password"   element={<ResetPassword />} />
              <Route path="/verify-email"     element={<VerifyEmail />} />
              <Route
                path="/onboarding"
                element={
                  <RouteShell name="Onboarding">
                    <ProtectedRoute requireOnboarded={false}>
                      <Onboarding />
                    </ProtectedRoute>
                  </RouteShell>
                }
              />
              <Route path="/_playground"      element={<RouteShell name="Playground"><Playground /></RouteShell>} />
              <Route path="/invite/:token"    element={<Invite />} />
              <Route path="/no-permission"    element={protect("Permission", <NoPermission />)} />
              <Route path="/"                 element={protect("Overview",   <Index />)} />
              <Route path="/orders"           element={protect("Orders",     <Orders />)} />
              <Route path="/alerts"           element={protect("Alerts",     <Alerts />)} />
              <Route path="/claude"           element={protect("Claude",     <ClaudePage />)} />
              <Route path="/settings"         element={protect("Settings",   <SettingsPage />)} />
              <Route
                path="/settings/team"
                element={
                  <RouteShell name="Team">
                    <ProtectedRoute>
                      <SettingsTeam />
                    </ProtectedRoute>
                  </RouteShell>
                }
              />
              <Route
                path="/settings/audit"
                element={
                  <RouteShell name="Audit">
                    <ProtectedRoute>
                      <SettingsAudit />
                    </ProtectedRoute>
                  </RouteShell>
                }
              />
              <Route path="/analytics"  element={protect("Analytics", <Analytics />)} />
              <Route path="/shipments"  element={protect("Shipments", <Shipments />)} />
              <Route path="/inventory"  element={protect("Inventory", <Inventory />)} />

              {/* Natural extensions — RTO-prevention / recovery surfaces */}
              <Route path="/risk"       element={protect("Risk scoring",   <RiskScoring />)} />
              <Route path="/prepaid"    element={protect("COD → Prepaid",  <PrepaidConversion />)} />
              <Route path="/routing"    element={protect("Auto routing",   <Routing />)} />
              <Route path="/ndr"        element={protect("NDR recovery",   <NDR />)} />
              <Route path="/freight"    element={protect("Freight recon",  <FreightRecon />)} />

              {/* Marketplace expansion — unified ops across Amazon / Flipkart / Meesho / Myntra */}
              <Route path="/channels"             element={protect("Channels",     <Channels />)} />
              <Route path="/channels/orders"      element={protect("Channel orders",      <ChannelOrders />)} />
              <Route path="/channels/returns"     element={protect("Channel returns",     <ChannelReturns />)} />
              <Route path="/channels/settlements" element={protect("Channel settlements", <ChannelSettlements />)} />

              <Route path="*"           element={<NotFound />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </AppErrorBoundary>
);

export default App;
